import { useEffect } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useAppDispatch, useAppSelector } from '@/store';
import type { RootState } from '@/store';
import type { Configuration } from '@/types';
import { Input, Select, Button } from '@/components/ui';
import { fetchConfigs, updateConfigs } from '@/store/thunks/configThunks';

const DAYS = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];

const configSchema = Yup.object({
  PRIX_VENTE: Yup.number().required('Prix de gasoil obligatoire'),
  DATE_JAUGE: Yup.string()
    .oneOf(DAYS, 'Jour invalide')
    .required('Date jauge obligatoire')
    .nullable(),
  NOTIF_NB: Yup.number().required('Nombre de jours notif obligatoire'),
  PERIODE_CALC_CITERNE: Yup.number().required('Période calcul obligatoire'),
  EQ_JOUR_HEUR: Yup.number().required('Jour = Heure obligatoire'),
});

function findConfig(configurations: Configuration[], nom: string): string | number {
  const conf = configurations.find((item) => item.nom === nom);
  if (!conf) return nom === 'DATE_JAUGE' ? DAYS[0] : 0;
  if (nom !== 'DATE_JAUGE') return parseFloat(conf.value) || 0;
  return conf.value;
}

export default function ConfigForm() {
  const dispatch = useAppDispatch();
  const { configurations } = useAppSelector((s: RootState) => s.config);

  useEffect(() => {
    dispatch(fetchConfigs());
  }, [dispatch]);

  const formik = useFormik({
    initialValues: {
      PRIX_VENTE: findConfig(configurations, 'PRIX_VENTE') as number,
      DATE_JAUGE: findConfig(configurations, 'DATE_JAUGE') as string,
      NOTIF_NB: findConfig(configurations, 'NOTIF_NB') as number,
      PERIODE_CALC_CITERNE: findConfig(configurations, 'PERIODE_CALC_CITERNE') as number,
      EQ_JOUR_HEUR: findConfig(configurations, 'EQ_JOUR_HEUR') as number,
    },
    validationSchema: configSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      const findItem = (nom: string) => configurations.find((c) => c.nom === nom);
      const payload: Configuration[] = Object.keys(values).map((key) => {
        const item = findItem(key);
        const value = values[key as keyof typeof values];
        return {
          id: item?.id,
          nom: key,
          value: String(value ?? ''),
        };
      });
      await dispatch(updateConfigs(payload));
    },
  });

  const { errors, touched, isSubmitting, handleSubmit, getFieldProps, setFieldValue } = formik;
  const dayOptions = DAYS.map((d) => ({ value: d, label: d }));

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <Input
          label="Prix vente HT"
          type="number"
          id="PRIX_VENTE"
          {...getFieldProps('PRIX_VENTE')}
          error={touched.PRIX_VENTE && errors.PRIX_VENTE ? errors.PRIX_VENTE : undefined}
        />
        <Select
          label="Date jauge"
          id="DATE_JAUGE"
          options={dayOptions}
          value={formik.values.DATE_JAUGE ?? ''}
          onChange={(e) => setFieldValue('DATE_JAUGE', e.target.value)}
          onBlur={() => formik.setFieldTouched('DATE_JAUGE', true)}
          error={touched.DATE_JAUGE && errors.DATE_JAUGE ? errors.DATE_JAUGE : undefined}
        />
        <Input
          label="Notification nb jours"
          type="number"
          id="NOTIF_NB"
          {...getFieldProps('NOTIF_NB')}
          error={touched.NOTIF_NB && errors.NOTIF_NB ? errors.NOTIF_NB : undefined}
        />
        <Input
          label="Période calcul"
          type="number"
          id="PERIODE_CALC_CITERNE"
          {...getFieldProps('PERIODE_CALC_CITERNE')}
          error={
            touched.PERIODE_CALC_CITERNE && errors.PERIODE_CALC_CITERNE
              ? errors.PERIODE_CALC_CITERNE
              : undefined
          }
        />
        <Input
          label="Jour = Heure"
          type="number"
          id="EQ_JOUR_HEUR"
          {...getFieldProps('EQ_JOUR_HEUR')}
          error={touched.EQ_JOUR_HEUR && errors.EQ_JOUR_HEUR ? errors.EQ_JOUR_HEUR : undefined}
        />
      </div>
      <div className="flex justify-end border-t border-slate-200 pt-4">
        <Button type="submit" variant="primary" loading={isSubmitting}>
          Enregistrer
        </Button>
      </div>
    </form>
  );
}
